import { Box, Stack, Typography } from "@mui/material";
import React, { useState } from "react";
import { FileUploadOutlined } from "@mui/icons-material";
import { IFileUpload, IFileUploadProps } from "../FileUpload.type";
import { getUniqueId } from "../../../helpers";
import { useDropzone } from "react-dropzone";
import { SingleFileDisplay } from "./SingleFileDisplay";
import { MultiFileDisplay } from "./MultiFileDisplay";

export const FileUpload: React.FC<IFileUploadProps> = (props) => {
  const {
    heading = "",
    value,
    multiple = false,
    disabled = false,
    deleteDisabled,
    onMultiChange,
    accept = "*",
    sx,
    hideUpload = false,
    variant = "NORMAL",
    galleryColumns = 3,
    galleryTileSize,
  } = props;

  const [localFile, setLocalFile] = React.useState<File | null>(null);

  const onDrop = React.useCallback(
    (acceptedFiles: File[]) => {
      if (disabled) return; // Add this check

      const files = acceptedFiles;
      if (files && files.length > 0 && !multiple) {
        const file = files[0];
        if (props.onChange) {
          setLocalFile(file);
          props.onChange(file);
        }
      } else if (
        files &&
        files.length > 0 &&
        Array.isArray(value) &&
        multiple
      ) {
        const file = files[0];

        if (props.onMultiChange) {
          props.onMultiChange([
            ...value,
            {
              key: getUniqueId(),
              file: files[0],
              name: files[0].name,
              path: null,
            },
          ]);
        }
      }
    },
    [value, disabled], // Add disabled to dependencies
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: disabled,
    noKeyboard: disabled,
  });

  const handleDelete = () => {
    setLocalFile(null);
    if (props.onDelete) {
      props.onDelete();
    }
  };

  const handleMultiChange = (data: IFileUpload[]) => {
    if (onMultiChange) {
      onMultiChange(data);
    }
  };

  if (!multiple && !Array.isArray(value)) {
    return (
      <Box width={"100%"}>
        <Typography fontWeight={600}>{heading}</Typography>

        {!hideUpload && !localFile && !value && (
          <Stack
            {...getRootProps()}
            marginTop={1}
            padding={2}
            direction={"row"}
            justifyContent={"center"}
            borderRadius={1}
            minHeight={"50px"}
            sx={(theme) => ({
              border: isDragActive
                ? `2px dashed ${theme.palette.primary.main}`
                : `2px dashed ${theme.palette.grey[500]}`,
              opacity: disabled ? 0.4 : 1,
              cursor: disabled ? "not-allowed" : "auto",
              pointerEvents: disabled ? "none" : "auto",
            })}
          >
            <input {...getInputProps()} accept={accept} disabled={disabled} />

            <Stack
              direction={"row"}
              justifyContent={"center"}
              spacing={1}
              sx={(theme) => ({
                cursor: "pointer",
                color: isDragActive
                  ? theme.palette.primary.main
                  : theme.palette.grey[900],
              })}
            >
              <FileUploadOutlined />
              <Typography variant="h5" fontWeight={700} sx={{}}>
                Browse Files
              </Typography>
            </Stack>
          </Stack>
        )}
        {(localFile || value) && (
          <Box marginTop={1}>
            <SingleFileDisplay
              file={localFile}
              value={value as string}
              onDelete={handleDelete}
              deleteDisabled={deleteDisabled}
            />
          </Box>
        )}
      </Box>
    );
  }

  return (
    <>
      <Box width={"100%"}>
        <Typography fontWeight={600}>{heading}</Typography>
        {!hideUpload && (
          <Stack
            {...getRootProps()}
            marginTop={1}
            padding={2}
            direction={"row"}
            justifyContent={"center"}
            borderRadius={1}
            border={"3px dotted gray"}
            minHeight={"50px"}
            sx={(theme) => ({
              border: isDragActive
                ? `2px dashed ${theme.palette.primary.main}`
                : `2px dashed ${theme.palette.grey[500]}`,
              opacity: disabled ? 0.4 : 1,
              cursor: disabled ? "not-allowed" : "auto",
            })}
          >
            <input {...getInputProps()} accept={accept} />

            <Stack
              direction={"row"}
              justifyContent={"center"}
              spacing={1}
              sx={(theme) => ({
                cursor: "pointer",
                color: isDragActive
                  ? theme.palette.primary.main
                  : theme.palette.grey[900],
              })}
            >
              <FileUploadOutlined />
              <Typography variant="h5" fontWeight={700}>
                Browse Files
              </Typography>
            </Stack>
          </Stack>
        )}
        {Array.isArray(value) && (
          <MultiFileDisplay
            value={value}
            onChange={handleMultiChange}
            deleteDisabled={deleteDisabled}
            variant={variant}
            galleryColumns={galleryColumns}
            galleryTileSize={galleryTileSize}
          />
        )}
      </Box>
    </>
  );
};
